/**
 * List of Songs Management — KARAOKE SONG LIST (v2)
 *
 * Talent uploads songs from local files here (any number). When a song is clicked,
 * it is activated in Sing4You and can be broadcast. Same visual language as
 * Sing4YouManagement_updated (dark stage theme, K palette).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { uploadFile } from "../../../services/api/uploadFile";

/* ─── palette (matches Sing4YouManagement_updated) ───────────────────────── */
const K = {
  bg: "#07070f",
  card: "#10101c",
  border: "rgba(168,85,247,0.22)",
  pink: "#ff6b9d",
  cyan: "#22d3ee",
  purple: "#a855f7",
  yellow: "#fbbf24",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.5)",
  red: "#E50914",
} as const;

/** First line: up to maxWords words; rest wrap below */
function formatTitleLines(title: string, maxWords = 5): React.ReactNode {
  if (!title || !String(title).trim()) return title;
  const words = String(title).trim().split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return title;
  const firstLine = words.slice(0, maxWords).join(" ");
  const rest = words.slice(maxWords).join(" ");
  return <>{firstLine}<br />{rest}</>;
}

/* ─── types ──────────────────────────────────────────────────────────────── */
export interface SongItem {
  id: string;
  name: string;
  /** Optional URL (e.g. after upload) */
  url?: string | null;
  /** Duration in seconds */
  duration?: number | null;
  /** Optional timestamp when added */
  addedAt?: number;
}

export interface ListofSongsManagementProps {
  /** All songs to display. Parent maintains this list. */
  songs: SongItem[];
  /** Optional: currently active song id (loaded in Sing4You) for highlight */
  activeSongId?: string | null;
  /** Called when a new song is added (after upload). Parent should append to list. */
  onSongAdded?: (song: SongItem) => void;
  /** Called when user selects a song — activates it in Sing4You for broadcast. */
  onSongSelect?: (song: SongItem) => void;
  /** Optional: callback when user requests play for a song (same as select for broadcast) */
  onPlayRequest?: (song: SongItem) => void;
  /** Optional: empty state message when list is empty */
  emptyMessage?: string;
}

function isValidAudioFile(f: File): boolean {
  const types = ["audio/mpeg","audio/wav","audio/ogg","audio/mp4","audio/aac","audio/webm","audio/flac","audio/x-m4a"];
  return types.includes(f.type) || !!f.name.toLowerCase().match(/\.(mp3|wav|ogg|m4a|aac|webm|flac)$/);
}

function fmt(s: number): string {
  if (!isFinite(s) || s < 0) return "0:00";
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
}

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function ListofSongsManagement({
  songs,
  activeSongId = null,
  onSongAdded,
  onSongSelect,
  onPlayRequest,
  emptyMessage = "Add songs using the upload area above, then click a song to load it in Karaoke Studio.",
}: ListofSongsManagementProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  /** Optimistic: show songs we just added so the list updates even before parent state propagates */
  const [optimisticAdded, setOptimisticAdded] = useState<SongItem[]>([]);
  const [lastSelectedName, setLastSelectedName] = useState<string | null>(null);
  const [lastSelectionStatus, setLastSelectionStatus] = useState<
    "idle" | "invalid" | "uploading" | "success" | "error"
  >("idle");

  const displaySongs = useMemo(() => {
    const fromParent = songs ?? [];
    const byId = new Map<string, SongItem>();
    fromParent.forEach((s) => byId.set(s.id, s));
    optimisticAdded.forEach((s) => byId.set(s.id, s));
    return Array.from(byId.values()).sort((a, b) => (a.addedAt ?? 0) - (b.addedAt ?? 0));
  }, [songs, optimisticAdded]);

  useEffect(() => {
    if (!songs?.length) return;
    const parentIds = new Set(songs.map((s) => s.id));
    setOptimisticAdded((prev) => prev.filter((s) => !parentIds.has(s.id)));
  }, [songs]);

  const handleRowClick = useCallback(
    (song: SongItem) => {
      onSongSelect?.(song);
    },
    [onSongSelect]
  );

  const handlePlayClick = useCallback(
    (e: React.MouseEvent, song: SongItem) => {
      e.stopPropagation();
      onPlayRequest?.(song);
      onSongSelect?.(song);
    },
    [onPlayRequest, onSongSelect]
  );

  const processFile = useCallback(
    async (file: File) => {
      console.log("[SongList] processFile: received file", {
        name: file.name,
        type: file.type,
        size: file.size,
      });
      setLastSelectedName(file.name);
      setLastSelectionStatus("idle");

      if (!isValidAudioFile(file)) {
        console.warn("[SongList] Invalid audio file selected", {
          name: file.name,
          type: file.type,
        });
        setUploadError("Invalid file. Use MP3, WAV, OGG, M4A, AAC, FLAC.");
        setLastSelectionStatus("invalid");
        return;
      }
      setUploadError(null);
      setUploading(true);
      setUploadProgress(0);
      setLastSelectionStatus("uploading");
      console.log("[SongList] Starting upload for", file.name);
      const tick = setInterval(() => setUploadProgress((p) => (p < 85 ? p + 10 : p)), 200);
      try {
        const url = await uploadFile(file);
        console.log("[SongList] Upload success", { name: file.name, url });
        clearInterval(tick);
        setUploadProgress(100);
        const urlStr = url && String(url).trim();
        if (!urlStr) {
          setUploadError("Upload succeeded but no URL returned.");
          setLastSelectionStatus("error");
          return;
        }
        const name = file.name.replace(/\.[^.]+$/, "");
        const song: SongItem = {
          id: `song-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          name,
          url: urlStr,
          duration: null,
          addedAt: Date.now(),
        };
        setOptimisticAdded((prev) => [...prev, song]);
        onSongAdded?.(song);
        /* This URL is stored in the list and used to load the MP3 in Sing4You when the song is clicked */
        setUploadError(null);
        setLastSelectionStatus("success");
        console.log("[SongList] Song added to list", song);
        // Duration will be set when the track is loaded in Sing4You (onTrackDuration)
      } catch (e) {
        clearInterval(tick);
        const message = e instanceof Error ? e.message : "Upload failed";
        setUploadError(message);
        setLastSelectionStatus("error");
        console.error("[SongList] Upload failed", { name: file.name, error: e });
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [onSongAdded]
  );

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length) return;
      console.log("[SongList] handleFileChange: selected files", files.length);
      const fileArray = Array.from(files);
      e.target.value = "";
      for (let i = 0; i < fileArray.length; i++) {
        console.log("[SongList] Processing file", i + 1, "of", fileArray.length, fileArray[i].name);
        await processFile(fileArray[i]);
      }
    },
    [processFile]
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const files = e.dataTransfer.files;
      if (!files?.length) return;
      console.log("[SongList] handleDrop: dropped files", files.length);
      const fileArray = Array.from(files);
      for (let i = 0; i < fileArray.length; i++) {
        console.log("[SongList] Processing file", i + 1, "of", fileArray.length, fileArray[i].name);
        await processFile(fileArray[i]);
      }
    },
    [processFile]
  );

  return (
    <Box
      sx={{
        bgcolor: K.bg,
        borderRadius: 2,
        overflow: "hidden",
        color: K.text,
        border: "1px solid",
        borderColor: K.border,
        position: "relative",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: K.border,
          bgcolor: "rgba(168,85,247,0.06)",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            bgcolor: "rgba(168,85,247,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AudioFileIcon sx={{ color: K.purple, fontSize: 20 }} />
        </Box>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "0.9rem",
            letterSpacing: 1.5,
            color: K.text,
          }}
        >
          SONG LIST
        </Typography>
        <Box
          sx={{
            ml: "auto",
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: "rgba(255,255,255,0.08)",
          }}
        >
          <Typography sx={{ fontSize: "0.72rem", color: K.muted }}>
            {displaySongs.length} {displaySongs.length === 1 ? "track" : "tracks"}
          </Typography>
        </Box>
      </Box>

      {/* Upload from local file — add any number of songs */}
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        sx={{
          mx: 1.5,
          mb: 1,
          border: "2px dashed",
          borderColor: dragOver ? K.pink : "rgba(168,85,247,0.35)",
          borderRadius: 1.5,
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          bgcolor: dragOver ? "rgba(255,107,157,0.05)" : "transparent",
          transition: "border-color 0.2s, background 0.2s",
        }}
      >
        <input
          accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.webm,.flac"
          style={{ display: "none" }}
          id="songlist-file-input"
          type="file"
          multiple
          onChange={handleFileChange}
          disabled={uploading}
        />
        <label htmlFor="songlist-file-input">
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 2,
              py: 0.75,
              borderRadius: 1.25,
              cursor: uploading ? "not-allowed" : "pointer",
              opacity: uploading ? 0.7 : 1,
              background: `linear-gradient(135deg, ${K.pink}, ${K.purple})`,
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.8rem",
              "&:hover": uploading ? {} : { opacity: 0.9 },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 18 }} />
            {uploading ? "Uploading…" : "Add song(s) from computer"}
          </Box>
        </label>
        {uploading && (
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              width: "100%",
              maxWidth: 200,
              height: 4,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.08)",
              "& .MuiLinearProgress-bar": {
                background: `linear-gradient(90deg, ${K.pink}, ${K.purple})`,
              },
            }}
          />
        )}
        <Typography sx={{ color: K.muted, fontSize: "0.7rem" }}>
          MP3 · WAV · FLAC · OGG · M4A · multiple files OK
        </Typography>
        {lastSelectedName && (
          <Typography sx={{ color: K.muted, fontSize: "0.7rem", mt: 0.5 }}>
            {lastSelectionStatus === "invalid" &&
              `Selected file "${lastSelectedName}" is not a supported audio type.`}
            {lastSelectionStatus === "uploading" &&
              `Uploading "${lastSelectedName}"…`}
            {lastSelectionStatus === "success" &&
              `Added "${lastSelectedName}" to the song list.`}
            {lastSelectionStatus === "error" &&
              `Failed to add "${lastSelectedName}". See console for details.`}
            {lastSelectionStatus === "idle" && !uploading && "File selected."}
          </Typography>
        )}
      </Box>

      {uploadError && (
        <Alert
          severity="warning"
          onClose={() => setUploadError(null)}
          sx={{
            mx: 1.5,
            mb: 1,
            py: 0.5,
            "& .MuiAlert-message": { fontSize: "0.8rem" },
          }}
        >
          {uploadError}
        </Alert>
      )}

      {/* Hint: selection loads song in Sing4You */}
        {displaySongs.length > 0 && onSongSelect && (
          <Typography sx={{ px: 2, py: 0.75, fontSize: "0.72rem", color: K.muted }}>
            Click a song to load it in Karaoke Studio (Sing4You) and broadcast.
          </Typography>
        )}

      {/* List area */}
      <Box
        sx={{
          maxHeight: 320,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { bgcolor: "rgba(255,255,255,0.04)" },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: K.purple + "60",
            borderRadius: 3,
          },
        }}
      >
        {displaySongs.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              borderBottom: "1px solid",
              borderColor: "transparent",
            }}
          >
            <Typography
              sx={{
                color: K.muted,
                fontSize: "0.8rem",
                lineHeight: 1.5,
              }}
            >
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          displaySongs.map((song, index) => {
            const isActive = activeSongId !== null && activeSongId !== undefined && song.id === activeSongId;
            return (
              <Box
                key={song.id}
                onClick={() => handleRowClick(song)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderBottom:
                    index < displaySongs.length - 1
                      ? "1px solid"
                      : "none",
                  borderColor: K.border,
                  bgcolor: isActive ? "rgba(168,85,247,0.12)" : "transparent",
                  cursor: onSongSelect || onPlayRequest ? "pointer" : "default",
                  transition: "background 0.2s ease",
                  "&:hover": {
                    bgcolor: isActive
                      ? "rgba(168,85,247,0.18)"
                      : "rgba(255,255,255,0.04)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    bgcolor: isActive ? "rgba(255,107,157,0.2)" : "rgba(168,85,247,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: isActive ? K.pink : K.muted,
                    }}
                  >
                    {index + 1}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      minWidth: 0,
                      wordBreak: "break-word",
                      color: K.text,
                    }}
                  >
                    ♪ {formatTitleLines(song.name)}
                  </Typography>
                  {song.duration != null && (
                    <Typography
                      sx={{
                        color: K.muted,
                        fontSize: "0.72rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {fmt(song.duration)}
                    </Typography>
                  )}
                </Box>
                {(onPlayRequest || onSongSelect) && (
                  <Tooltip title="Load in Karaoke Studio (Sing4You)">
                    <IconButton
                      size="small"
                      onClick={(e) => handlePlayClick(e, song)}
                      sx={{
                        color: K.muted,
                        "&:hover": {
                          color: K.pink,
                          bgcolor: "rgba(255,107,157,0.12)",
                        },
                      }}
                    >
                      <PlayArrowIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
